import React, { useContext, useEffect, useState } from 'react';
import { ServiceResponse } from '../../app/services/shared/interfaces';
import Breadcrumbs, { BreadcrumbsItem } from '../../template/MainTheme/components/Breadcrumbs';
import DefaultCard from '../../components/default/DefaultCard';
import DefaultModal from '../../components/default/DefaultModal';
import DefaultToolBar from '../../components/default/DefaultToolBar';
import ButtonCreate from '../../components/buttons/ButtonCreate';
import { toast } from 'react-toastify';
import useReactConfirmAlert from '../../hooks/useReactConfirmAlert';
import { AppContext } from '../../contexts/AppContext';
import useCountryService from '../../app/services/hooks/useCountryService';
import { CountryDataForm, CountryFilters } from '../../app/models/Country';
import CountryDataTable from './components/tables/CountryDataTable';
import CountryCreate from './components/CountryCreate';
import CountryEdit from './components/CountryEdit';
import useNavigationPage from '../../hooks/useNavigationPage';

const breadcrumbs: BreadcrumbsItem[] = [
    { name: 'Países', url: '/countries', isActive: true }
];

const Countries = () => {
    const { showLoading, hideLoading } = useContext(AppContext);
    const { navigationPage } = useNavigationPage();
    const {
        fetchingGetCountries,
        getCountries,
        deleteCountry
    } = useCountryService();

    const [countries, setCountries] = useState<CountryDataForm[]>([]);
    const [totalRows, setTotalRows] = useState(0);
    const [countryIdEdit, setCountryIdEdit] = useState<number>(-1);
    const [showingCreate, setShowingCreate] = useState(false);
    const [showingEdit, setShowingEdit] = useState(false);

    const [filter, setFilter] = useState<CountryFilters>({
        page: 1,
        per_page: 10,
        sort: 'id',
        order: 'desc'
    });

    useEffect(() => {
        reloadTable();
    }, [filter.page, filter.per_page, filter.sort, filter.order]);

    const reloadTable = () => {
        getCountries(filter, {
            onSuccess: (response: ServiceResponse) => {
                setCountries(response.data.countries || []);
                setTotalRows(response.data.total_rows || 0);
            },
            onError: (response: ServiceResponse) => {
                toast.error(response.message, { autoClose: 2000 });
                if (response.data?.to_dashboard) navigationPage('/');
            }
        });
    };

    const showEdit = (id: number) => {
        setCountryIdEdit(id);
        setShowingEdit(true);
    };

    const hideEdit = () => {
        setShowingEdit(false);
        setCountryIdEdit(-1);
    };

    const destroy = (id: number) => {
        useReactConfirmAlert().requestConfirmation({
            title: '¿Estás seguro?',
            message: 'Está a punto de eliminar este país.',
            buttons: {
                confirmButton: {
                    label: 'Sí, confirmar',
                    onClick: () => {
                        if (showLoading) showLoading('loading', 'Eliminando país...');
                        deleteCountry(id, {
                            onSuccess: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().successAlert({ title: 'Éxito', message: response.message });
                                reloadTable();
                            },
                            onError: (response: ServiceResponse) => {
                                if (hideLoading) hideLoading();
                                useReactConfirmAlert().errorAlert({ title: 'Error', message: response.message });
                            }
                        });
                    }
                },
                cancelButton: {
                    label: 'No, cancelar',
                    onClick: () => { /* usuario canceló */ }
                }
            }
        });
    };

    return (
        <>
            <Breadcrumbs pageSection="Países" breadcrumbs={breadcrumbs} />
            <DefaultCard>
                <DefaultToolBar left={<ButtonCreate callbackCreate={() => setShowingCreate(true)} title="Nuevo país" />} />
                <CountryDataTable
                    countries={countries}
                    totalRows={totalRows}
                    loading={fetchingGetCountries}
                    filter={filter}
                    setFilter={setFilter}
                    edit={showEdit}
                    destroy={destroy}
                    callbackSuccess={reloadTable}
                />
            </DefaultCard>

            {showingCreate && (
                <DefaultModal
                    show={showingCreate}
                    handleClose={() => setShowingCreate(false)}
                    title="Nuevo país"
                    backdrop
                    showFooter={false}
                >
                    <CountryCreate
                        onSaved={() => { reloadTable(); setShowingCreate(false); }}
                        onCancel={() => setShowingCreate(false)}
                        onError={() => setShowingCreate(false)}
                    />
                </DefaultModal>
            )}

            {showingEdit && countryIdEdit > 0 && (
                <DefaultModal
                    show={showingEdit}
                    handleClose={hideEdit}
                    title="Editar país"
                    backdrop
                    showFooter={false}
                >
                    <CountryEdit
                        countryId={countryIdEdit}
                        onSaved={() => { reloadTable(); hideEdit(); }}
                        onCancel={hideEdit}
                        onError={hideEdit}
                    />
                </DefaultModal>
            )}
        </>
    );
};

export default Countries;
